import { useMemo } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import parsePhoneNumber from 'libphonenumber-js';
import { MarketCountryCode } from '@axo/shared/types';
import { isBrand } from '@axo/config/@types/Brand.types';
import { Product } from '@axo/shared/data-access/types';
import {
  SECURED_LOAN_AMOUNT_LIMITS,
  UNSECURED_LOAN_AMOUNT_LIMITS,
} from './constants';

const CreateApplicationFormSchema = z.object({
  Application: z
    .object({
      AppliedAmount: z.number({ invalid_type_error: 'Invalid number' }),
      LoanDuration: z
        .number({ invalid_type_error: 'Invalid number' })
        .min(1)
        .max(25),
      MarketCountry: z.nativeEnum(MarketCountryCode),
      Product: z.nativeEnum(Product),
      Meta: z.object({
        Brand: z.string().superRefine((value, ctx) => {
          if (!isBrand(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'You must select a brand',
            });
          }
        }),
      }),
    })
    .transform((values, ctx) => {
      const LOAN_AMOUNT_LIMITS = (
        values.Product === Product.SecuredLoan
          ? SECURED_LOAN_AMOUNT_LIMITS
          : UNSECURED_LOAN_AMOUNT_LIMITS
      )[values.MarketCountry];

      if (
        values.AppliedAmount < LOAN_AMOUNT_LIMITS.min ||
        values.AppliedAmount > LOAN_AMOUNT_LIMITS.max
      ) {
        ctx.addIssue({
          path: ['AppliedAmount'],
          code: z.ZodIssueCode.custom,
          message: `Applied amount must be between ${LOAN_AMOUNT_LIMITS.min} and ${LOAN_AMOUNT_LIMITS.max}`,
        });
        return z.NEVER;
      }

      return {
        ...values,
        AppliedAmount:
          values.AppliedAmount -
          (values.AppliedAmount % LOAN_AMOUNT_LIMITS.step),
      };
    }),
  Person: z.object({
    SocialSecurityNumber: z.string().trim().min(1, 'Required'),
  }),
  Customer: z.object({
    Email: z.string().email(),
    MobilePhone: z
      .object({
        Number: z.string({ required_error: 'Required' }),
        CountryCode: z.number({ required_error: 'Required' }),
      })
      .superRefine((values, ctx) => {
        try {
          const phone = parsePhoneNumber(values.Number ?? '', {
            defaultCallingCode: values.CountryCode
              ? String(values.CountryCode)
              : '',
          });

          if (!phone || !phone.isValid()) {
            throw new Error('Invalid phone number');
          }
        } catch (e) {
          if (e instanceof Error) {
            ctx.addIssue({
              path: ['Number'],
              code: z.ZodIssueCode.custom,
              message: e.message,
            });
          }
        }
      }),
  }),
});

export const useCreateApplicationFormSchema = () => {
  return CreateApplicationFormSchema;
};

export const useCreateApplicationFormSchemaResolver = () => {
  const schema = useCreateApplicationFormSchema();

  return useMemo(
    () => zodResolver(schema, { async: true }, { mode: 'async' }),
    [schema],
  );
};

export type CreateApplicationFormDataValues = z.infer<
  ReturnType<typeof useCreateApplicationFormSchema>
>;
